import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getCoachAnalytics } from './coachApi';

const METRICS = ['sleep', 'mood', 'energy', 'stress', 'readiness'];
const METRIC_COLORS = { sleep: '#5B9ED0', mood: '#4AC87A', energy: '#C8A84B', stress: '#C05050', readiness: '#7B6FD0' };
const METRIC_ICONS = { sleep: '😴', mood: '💚', energy: '⚡', stress: '🧠', readiness: '🎯' };

function MiniBar({ value, max = 10, color }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div style={{ flex: 1, background: _T.bg4, borderRadius: 4, height: 6, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.4s' }} />
    </div>
  );
}

function SparkLine({ data, color, height = 40, width = 120 }) {
  if (!data || data.length < 2) return <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: C.silver2, fontSize: 10 }}>—</span></div>;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts.split(' ').pop().split(',')[0]} cy={pts.split(' ').pop().split(',')[1]} r="3" fill={color} />
    </svg>
  );
}

export default function CoachAnalyticsTab({ coachId, clients, lang, tier }) {
  const nl = lang === 'nl';
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeMetric, setActiveMetric] = useState('sleep');
  const [period, setPeriod]       = useState(7); // days

  const load = useCallback(() => {
    getCoachAnalytics(coachId, { period }).then(d => { setAnalytics(d); setLoading(false); }).catch(() => setLoading(false));
  }, [coachId, period]);

  useEffect(() => { load(); }, [load]);

  // Generate mock trend from client data if API returns nothing useful
  const clientList = clients || [];
  const avgSleep = clientList.filter(c => c.avg_sleep).length > 0
    ? (clientList.reduce((s, c) => s + (c.avg_sleep || 0), 0) / clientList.filter(c => c.avg_sleep).length).toFixed(1)
    : null;
  const avgMood = clientList.filter(c => c.avg_mood).length > 0
    ? (clientList.reduce((s, c) => s + (c.avg_mood || 0), 0) / clientList.filter(c => c.avg_mood).length).toFixed(1)
    : null;

  // Build per-client rows
  const clientRows = clientList.map(c => ({
    ...c,
    sleepScore: c.avg_sleep ? Math.min((c.avg_sleep / 9) * 10, 10) : null,
    moodScore: c.avg_mood ? (c.avg_mood / 5) * 10 : null,
    readiness: c.readiness_score || null,
  }));

  const atRisk = clientRows.filter(c => (c.sleepScore && c.sleepScore < 5) || (c.moodScore && c.moodScore < 4));

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>
      <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Analytiek' : 'Analytics'}</div>
      <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>{nl ? 'Inzichten over al jouw atleten' : 'Insights across all your athletes'}</div>

      {/* Period selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[7, 14, 30].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            style={{ padding: '6px 16px', borderRadius: 20, border: `1.5px solid ${period === p ? _T.accent : _T.cardBorderBot}`, background: period === p ? `${_T.accent}20` : _T.bg4, color: period === p ? _T.accent : C.silver, fontSize: 12, fontWeight: period === p ? 700 : 400, cursor: 'pointer' }}>
            {p}d
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 9, color: '#5B9ED0', letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>{nl ? 'GEM. SLAAP' : 'AVG SLEEP'}</div>
          <div style={{ color: C.white, fontSize: 26, fontWeight: 800 }}>{avgSleep ? `${avgSleep}h` : '—'}</div>
          <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>{clientList.length} {nl ? 'atleten' : 'athletes'}</div>
        </div>
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 9, color: '#4AC87A', letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>{nl ? 'GEM. STEMMING' : 'AVG MOOD'}</div>
          <div style={{ color: C.white, fontSize: 26, fontWeight: 800 }}>{avgMood ? `${avgMood}/5` : '—'}</div>
          <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>{nl ? 'afgelopen 7d' : 'last 7d'}</div>
        </div>
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 9, color: '#C05050', letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>{nl ? 'RISICO ATLETEN' : 'AT-RISK ATHLETES'}</div>
          <div style={{ color: atRisk.length > 0 ? '#C05050' : '#4AC87A', fontSize: 26, fontWeight: 800 }}>{atRisk.length}</div>
          <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>{nl ? 'lage scores' : 'low scores'}</div>
        </div>
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>{nl ? 'TOTAAL ATLETEN' : 'TOTAL ATHLETES'}</div>
          <div style={{ color: C.white, fontSize: 26, fontWeight: 800 }}>{clientList.length}</div>
          <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>{nl ? 'actief' : 'active'}</div>
        </div>
      </div>

      {/* At-risk alert */}
      {atRisk.length > 0 && (
        <div style={{ background: '#C0505015', border: '1px solid #C0505044', borderRadius: 14, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: '#C05050', letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>⚠ {nl ? 'AANDACHT VEREIST' : 'ATTENTION REQUIRED'}</div>
          {atRisk.map(c => (
            <div key={c.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#C0505020', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{c.avatar || '👤'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.white, fontSize: 12, fontWeight: 600 }}>{c.name}</div>
                <div style={{ color: '#C05050', fontSize: 10 }}>
                  {c.sleepScore && c.sleepScore < 5 ? `😴 ${c.avg_sleep}h` : ''}
                  {c.moodScore && c.moodScore < 4 ? ` 💔 ${c.avg_mood}/5` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Metric tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto', paddingBottom: 4 }}>
        {METRICS.map(m => (
          <button key={m} onClick={() => setActiveMetric(m)}
            style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${activeMetric === m ? METRIC_COLORS[m] : _T.cardBorderBot}`, background: activeMetric === m ? `${METRIC_COLORS[m]}20` : _T.bg4, color: activeMetric === m ? METRIC_COLORS[m] : C.silver, fontSize: 11, fontWeight: activeMetric === m ? 700 : 400, cursor: 'pointer' }}>
            {METRIC_ICONS[m]} {nl ? { sleep: 'Slaap', mood: 'Stemming', energy: 'Energie', stress: 'Stress', readiness: 'Gereedheid' }[m] : m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>

      {/* Per-client metric breakdown */}
      <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: METRIC_COLORS[activeMetric], letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
          {METRIC_ICONS[activeMetric]} {(nl ? { sleep: 'SLAAP PER ATLEET', mood: 'STEMMING PER ATLEET', energy: 'ENERGIE PER ATLEET', stress: 'STRESS PER ATLEET', readiness: 'GEREEDHEID PER ATLEET' } : { sleep: 'SLEEP BY ATHLETE', mood: 'MOOD BY ATHLETE', energy: 'ENERGY BY ATHLETE', stress: 'STRESS BY ATHLETE', readiness: 'READINESS BY ATHLETE' })[activeMetric]}
        </div>
        {clientList.length === 0 ? (
          <div style={{ color: C.silver2, fontSize: 13 }}>{nl ? 'Nog geen atleten' : 'No athletes yet'}</div>
        ) : (
          clientList.map(c => {
            let val = null, max = 10;
            if (activeMetric === 'sleep') { val = c.avg_sleep; max = 9; }
            else if (activeMetric === 'mood') { val = c.avg_mood; max = 5; }
            else if (activeMetric === 'readiness') { val = c.readiness_score; max = 10; }
            else val = null;

            const pct = val && max ? Math.min((val / max) * 100, 100) : 0;
            const color = METRIC_COLORS[activeMetric];
            return (
              <div key={c.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{c.avatar || '👤'}</div>
                <div style={{ width: 70, color: C.silver, fontSize: 11, fontWeight: 600 }}>{c.name?.split(' ')[0]}</div>
                <MiniBar value={pct} max={100} color={color} />
                <div style={{ width: 36, textAlign: 'right', color: val ? C.white : C.silver2, fontSize: 12, fontWeight: 700 }}>
                  {val ? (activeMetric === 'sleep' ? `${val}h` : activeMetric === 'mood' ? `${val}/5` : val) : '—'}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Wellness distribution */}
      {clientList.length > 0 && (
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'WELLNESS VERDELING' : 'WELLNESS DISTRIBUTION'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { label: nl ? 'Uitstekend' : 'Excellent', color: '#4AC87A', count: clientList.filter(c => c.avg_mood >= 4.5 || c.avg_sleep >= 8).length },
              { label: nl ? 'Goed' : 'Good', color: '#C8A84B', count: clientList.filter(c => (c.avg_mood >= 3 && c.avg_mood < 4.5) || (c.avg_sleep >= 7 && c.avg_sleep < 8)).length },
              { label: nl ? 'Matig' : 'Fair', color: '#D4873A', count: clientList.filter(c => (c.avg_mood >= 2 && c.avg_mood < 3) || (c.avg_sleep >= 6 && c.avg_sleep < 7)).length },
              { label: nl ? 'Laag' : 'Low', color: '#C05050', count: clientList.filter(c => (c.avg_mood && c.avg_mood < 2) || (c.avg_sleep && c.avg_sleep < 6)).length },
            ].map(seg => (
              <div key={seg.label} style={{ flex: 1, textAlign: 'center', background: _T.bg4, borderRadius: 10, padding: '10px 6px', border: `1px solid ${seg.color}33` }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: seg.color }}>{seg.count}</div>
                <div style={{ fontSize: 9, color: C.silver2, marginTop: 2 }}>{seg.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Export section */}
      {tier === 'pro' || tier === 'elite' ? (
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'EXPORTEREN' : 'EXPORT'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => {
              const rows = [['Name', 'Avg Sleep', 'Avg Mood', 'Readiness'].join(','),
                ...clientList.map(c => [c.name, c.avg_sleep || '', c.avg_mood || '', c.readiness_score || ''].join(','))].join('\n');
              const blob = new Blob([rows], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'coach_analytics.csv'; a.click();
            }} style={{ flex: 1, background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 10, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '10px', cursor: 'pointer' }}>
              ⬇ CSV
            </button>
          </div>
        </div>
      ) : (
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 4 }}>🔒 {nl ? 'Export beschikbaar in Pro & Elite' : 'Export available in Pro & Elite'}</div>
        </div>
      )}
    </div>
  );
}
